/**
 * Scout by EQ - Main Application Router
 * 
 * Claude/ChatGPT-style project-based architecture.
 * 
 * Routes:
 * - / : Scout landing page (marketing)
 * - /app : Feed creation (the app IS the input)
 * - /app/feed/:id : Inside a feed (leads + refinement)
 * - /app/settings : Unified settings
 * - /share/:slugToken : Public share pages (growth loop)
 * 
 * Flow: Create Feed → Scout Watches → Leads Appear → Contact
 * 
 * Design: EQ.app Brand Guidelines
 * - Typography: Playfair Display (serif)
 * - Primary: #5a20ff, Secondary: #52dcb3
 * - Grain overlay, smooth animations
 */

import React from "react";
import { Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

// Scout Pages (Marketing/Landing)
import ScoutHome from "./components/scout/ScoutHome";

// Feed Pages (Core App Experience - Claude/ChatGPT Style)
import FeedHome from "./components/feed/FeedHome";
import FeedWorkspace from "./components/feed/FeedWorkspace";

// Legacy Inbox (preserved for backwards compatibility)
import { LeadInbox } from "./components/betterleads";

// Lead Inbox Lite (new)
import LeadInboxLite from "./components/leadInboxLite/LeadInboxLite";
import LeadInboxItem from "./components/leadInboxLite/LeadInboxItem";

// Auth & Protection
import ProtectedRoute from "./components/ProtectedRoute";

// Share Pages (public-facing growth loop)
import ShareResultsPage from "./components/share/ShareResultsPage";

// Legal pages (required for compliance)
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import TermsOfService from "./components/legal/TermsOfService";
import CookiePolicy from "./components/legal/CookiePolicy";

// Learn pages
import { LearnHub } from "./components/learn";

// Settings & Billing
import SettingsPage from "./components/dashboard/SettingsPage";
import BillingSuccess from "./components/billing/BillingSuccess";

// Public Pricing
import PricingPage from "./components/pricing/PricingPage";

// Onboarding
import OnboardingPage from "./components/onboarding/OnboardingPage";

// Outreach
import OutreachPage from "./components/outreach/OutreachPage";

// Invite landing page
import InviteLanding from "./components/invite/InviteLanding";

// Deep Research component kept for standalone access if needed
// Main demand signals flow is now integrated into FeedHome search modes

// Shared Components
import CookieConsentBanner from "./components/shared/CookieConsentBanner";
import TermsAcceptanceModal from "./components/shared/TermsAcceptanceModal";
import { ToastProvider } from "./components/shared/Toast";
import AppTour from "./components/ui/AppTour";
import GlobalHeader from "./components/ui/GlobalHeader";
import GlobalSidebar from "./components/ui/GlobalSidebar";
import { QuickSearchProvider } from "./contexts/QuickSearchContext";
import { SearchPathProvider } from "./contexts/SearchPathContext";
import { NavigationProvider } from "./contexts/NavigationContext";

// Feature flag: only enable experimental features when env var is set
const EXPERIMENTAL = process.env.REACT_APP_SCOUT_EXPERIMENTAL === 'true';

// Smart redirect that waits for Auth0 callback processing
function SmartHomeRedirect() {
  const [searchParams] = useSearchParams();
  const { isLoading } = useAuth0();
  
  // If there's a code parameter, Auth0 is processing the callback
  // Wait for Auth0 to finish before redirecting
  const hasAuthCode = searchParams.has('code') && searchParams.has('state');
  
  if (hasAuthCode || isLoading) {
    // Show nothing while Auth0 processes the callback
    return null;
  }
  
  return <Navigate to="/app" replace />;
}

function App() {
  return (
    <ToastProvider>
    <SearchPathProvider>
    <QuickSearchProvider>
      <Routes>
        {/* ============================================ */}
        {/* PUBLIC ROUTES                               */}
        {/* ============================================ */}
        
        {/* Root redirects to /app - waits for Auth0 callback if present */}
        <Route path="/" element={<SmartHomeRedirect />} />
        
        {/* Share Pages - Public growth loop with EQ.app branding */}
        <Route path="/share/:slugToken" element={<ShareResultsPage />} />
        <Route path="/share/:slugToken/brief" element={<ShareResultsPage mode="brief" />} />
        {/* Legacy feed share URLs - redirect to new format */}
        <Route path="/share/feed/:feedId" element={<ShareResultsPage />} />
        <Route path="/share/feed/:feedId/brief" element={<ShareResultsPage mode="brief" />} />
        
        {/* Invite Landing - Public branded page for team invitations */}
        <Route path="/invite/:token" element={<InviteLanding />} />

        {/* Pricing Page - Public */}
        <Route path="/pricing" element={<PricingPage />} />
        
        {/* Legal Pages */}
        <Route path="/legal/privacy" element={<PrivacyPolicy />} />
        <Route path="/legal/terms" element={<TermsOfService />} />
        <Route path="/legal/cookies" element={<CookiePolicy />} />
        
        {/* ============================================ */}
        {/* LEARN - How Scout Works                     */}
        {/* ============================================ */}
        <Route path="/learn" element={<LearnHub />} />
        
        {/* ============================================ */}
        {/* APP ROUTES                                  */}
        {/* Main app is accessible to guests (gated)    */}
        {/* ============================================ */}
        
        {/* Lead Inbox Lite (tenant-scoped) - experimental */}
        {EXPERIMENTAL && (
          <>
            <Route 
              path="/lead-inbox" 
              element={<ProtectedRoute component={LeadInboxLite} />} 
            />
            <Route 
              path="/lead-inbox/item/:id" 
              element={<ProtectedRoute component={LeadInboxItem} />} 
            />
            <Route 
              path="/app/lead-inbox" 
              element={<ProtectedRoute component={LeadInboxLite} />} 
            />
            <Route 
              path="/app/lead-inbox/item/:id" 
              element={<ProtectedRoute component={LeadInboxItem} />} 
            />
          </>
        )}

        {/* Main App - Feed Home (Claude/ChatGPT style) */}
        {/* Now accessible to guests - card clicks are gated */}
        <Route 
          path="/app" 
          element={<FeedHome />} 
        />
        
        {/* Inside a Feed - Workspace with leads */}
        <Route 
          path="/app/feed/:feedId" 
          element={<ProtectedRoute component={FeedWorkspace} />} 
        />
        
        {/* Legacy Inbox - DEPRECATED: Redirects to main feed 
        <Route 
          path="/app/inbox" 
          element={<ProtectedRoute component={LeadInbox} />} 
        />
        */}
        <Route path="/app/inbox" element={<Navigate to="/app" replace />} />
        
        {/* Onboarding - Tensol-style guided setup - experimental */}
        {EXPERIMENTAL && (
          <Route
            path="/app/onboarding"
            element={<ProtectedRoute component={OnboardingPage} />}
          />
        )}

        {/* Settings - Unified settings hub */}
        <Route 
          path="/app/settings" 
          element={<ProtectedRoute component={SettingsPage} />} 
        />
        <Route 
          path="/app/settings/billing/success" 
          element={<ProtectedRoute component={BillingSuccess} />} 
        />
        
        {/* Reach - Personalized outreach module */}
        <Route 
          path="/app/reach" 
          element={<ProtectedRoute component={OutreachPage} />} 
        />
        <Route path="/dashboard/outreach" element={<Navigate to="/app/reach" replace />} />
        
        {/* Deep Research redirects to main app with demand mode */}
        <Route 
          path="/app/research" 
          element={<Navigate to="/app?mode=demand" replace />} 
        />
        
        {/* ============================================ */}
        {/* LEGACY REDIRECTS - Preserve old URLs        */}
        {/* ============================================ */}
        
        {/* Old dashboard routes → main feed */}
        <Route path="/dashboard" element={<Navigate to="/app" replace />} />
        <Route path="/dashboard/*" element={<Navigate to="/app" replace />} />
        
        {/* Old settings routes */}
        <Route path="/dashboard/settings" element={<Navigate to="/app/settings" replace />} />
        <Route path="/dashboard/settings/*" element={<Navigate to="/app/settings" replace />} />
        
        {/* Catch-all → landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Global Components - wrapped in NavigationProvider for mobile sidebar state */}
      <NavigationProvider>
        <GlobalHeader />
        <GlobalSidebar />
      </NavigationProvider>
      <CookieConsentBanner />
      <TermsAcceptanceModal />
      <AppTour />
    </QuickSearchProvider>
    </SearchPathProvider>
    </ToastProvider>
  );
}

export default App;
